import { Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { OverlayMenu, OverlayMenuOption } from '../../OverlayMenu';
import {
    defaultPETColorMap,
    PETColorMap,
    selectedPETColorMappingState,
} from '~/state/atoms/image/petImageAtom';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';

const PET_COLOR_MAPPINGS: PETColorMap[] = [
    {
        id: 1,
        name: 'Green Fire',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.0] },
            { position: 0.333, color: [0.0, 1.0, 0.0] },
            { position: 0.667, color: [1.0, 1.0, 0.0] },
            { position: 1.0, color: [1.0, 1.0, 1.0] },
        ],
        previewGradient:
            'linear-gradient(to right, #000000 0%, #00ff00 33%, #ffff00 67%, #ffffff 100%)',
    },
    {
        id: 2,
        name: 'Rainbow',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.0] },
            { position: 0.25, color: [0.0, 0.0, 1.0] },
            { position: 0.5, color: [0.0, 1.0, 1.0] },
            { position: 0.75, color: [1.0, 1.0, 0.0] },
            { position: 1.0, color: [1.0, 0.0, 0.0] },
        ],
        previewGradient:
            'linear-gradient(to right, #000000 0%, #0000ff 25%, #00ffff 50%, #ffff00 75%, #ff0000 100%)',
    },
    {
        id: 3,
        name: 'Rainbow 2',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.04] },
            { position: 0.25, color: [0.32, 0.06, 0.44] },
            { position: 0.5, color: [0.73, 0.21, 0.33] },
            { position: 0.75, color: [0.99, 0.56, 0.15] },
            { position: 1.0, color: [0.99, 0.99, 0.64] },
        ],
        previewGradient:
            'linear-gradient(to right, #00000a 0%, #520b70 25%, #ba3654 50%, #fc8f26 75%, #fcfca3 100%)',
    },
    {
        id: 4,
        name: 'Spectrum',
        stops: [
            { position: 0.0, color: [0.0, 0.0, 0.0] },
            { position: 0.1, color: [0.27, 0.0, 0.33] },
            { position: 0.325, color: [0.28, 0.14, 0.47] },
            { position: 0.55, color: [0.13, 0.37, 0.52] },
            { position: 0.775, color: [0.37, 0.65, 0.35] },
            { position: 1.0, color: [0.99, 0.91, 0.14] },
        ],
        previewGradient:
            'linear-gradient(to right, #000000 0%, #440054 10%, #472478 32.5%, #215e85 55%, #5ea659 77.5%, #fce823 100%)',
    },
    {
        id: 5,
        name: 'Invert Gray',
        stops: [
            { position: 0.0, color: [1.0, 1.0, 1.0] },
            { position: 1.0, color: [0.0, 0.0, 0.0] },
        ],
        previewGradient: 'linear-gradient(to right, #ffffff 0%, #000000 100%)',
    },
];

const allFusionColorMappings = [defaultPETColorMap, ...PET_COLOR_MAPPINGS];

const ColorMapPreview = ({ gradient }: { gradient: string }) => (
    <Box
        sx={{
            width: 16,
            height: 10,
            mr: 0.5,
            borderRadius: 0.5,
            background: gradient,
            flexShrink: 0,
        }}
    />
);

const petColorMapOptions: OverlayMenuOption<number>[] =
    allFusionColorMappings.map((colorMap) => ({
        value: colorMap.id,
        label: colorMap.name,
        icon: <ColorMapPreview gradient={colorMap.previewGradient} />,
    }));

const PetFusionColorSelector = () => {
    const selectedColorMap = useRecoilValue(selectedPETColorMappingState);
    const { updateSelectedPETFusionColorMapping } = useViewportEnvironment();

    const handleChange = (id: number) => {
        const colorMap = allFusionColorMappings.find((cm) => cm.id === id);
        if (colorMap && colorMap.id !== selectedColorMap.id) {
            updateSelectedPETFusionColorMapping(colorMap);
        }
    };

    return (
        <OverlayMenu
            options={petColorMapOptions}
            value={selectedColorMap.id}
            onChange={handleChange}
            icon={
                <ColorMapPreview gradient={selectedColorMap.previewGradient} />
            }
            renderLabel={() => selectedColorMap.name}
        />
    );
};

export default PetFusionColorSelector;
